import { NodeService } from "./../../../../core/services/node.service";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { Node } from "src/app/core/models/node";
import { AuthenticationService } from "../../../../core/services/authentication.service";
import {
  filter,
  skip,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from "rxjs";
import { NgxSpinnerService } from "ngx-spinner";
import { ManageTooltipService } from "src/app/core/services/manage-tooltip.service";
import { ApplicationService } from "../../../../core/services/application.service";
import { MatDialog } from "@angular/material/dialog";
import { SetNodegroupVmComponent } from "../../modals/set-nodegroup-vm/set-nodegroup-vm.component";
import { UserRoleNames } from "../../../../core/models/users";
import { CloudTypes, NodeTypes } from "../../../../core/models/globals";
import { ActivatedRoute } from "@angular/router";
import { UpdateBmNodeComponent } from "../../modals/update-bm-node/update-bm-node.component";
import { ConfigEntityService } from "../../../../core/services/config-entity.service";

@Component({
  selector: "app-node-details",
  templateUrl: "./node-details.component.html",
  styleUrls: ["./node-details.component.scss"],
})
export class NodeDetailsComponent implements OnInit, OnDestroy {
  depName;
  nodeDetailsSub: Subscription;
  billingDetailsSub: Subscription;
  showBilledHours: boolean;
  reloadNodeDetails: Subscription;
  tooltipHtml = "";
  nodeGroups = [];
  role: string = "";
  loggedInUser: string = this.authenticationService.currentUserValue.username;
  nodeGroupChangeAccess: boolean = false;
  rdProtocolChangeAccess: boolean = false;
  node: any = null;
  nodeDepName: string;
  isMonitor: boolean =
    this.authenticationService.currentUserValue.role == UserRoleNames.Reader
      ? true
      : false;
  showEdit: boolean = false;
  cloudType: string;
  isxCAT: boolean = true;
  rdProtocol: string;
  rdProtocolName: string;
  isGlobalAdmin =
    this.authenticationService.getRole() == UserRoleNames.GlobalAdmin;
  nodeNetwork = null;
  destroy$ = new Subject<void>();

  constructor(
    private nodeService: NodeService,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private tooltipJson: ManageTooltipService,
    private applicationService: ApplicationService,
    private dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private configEntityService: ConfigEntityService
  ) {}

  ngOnInit(): void {
    this.role = this.authenticationService.getRole() || "";
    if (this.role == UserRoleNames.GlobalAdmin) {
      this.nodeGroupChangeAccess = true;
    }
    this.spinner.show();
    // Subscribe to selected node changes and trigger network info fetch
    this.nodeService.SelectedNode.pipe(
      takeUntil(this.destroy$),
      filter((x) => x != null),
      switchMap((node) => {
        this.nodeNetwork = null;
        this.loadNodeDetails(node);
        // Return the network info observable
        return this.nodeService.getNodeNetworkInfo(node.vmName);
      })
    ).subscribe({
      next: (data) => {
        this.nodeNetwork = JSON.parse(JSON.stringify(data));
      },
      error: (error) => {
        console.error("Error fetching network info:", error);
        this.nodeNetwork = null;
      },
    });
    this.getNodeGroup();
    this.billingDetailsSub = this.nodeService.ShowBillingDetails.subscribe(
      (value) => {
        this.showBilledHours = value;
      }
    );
  }

  ngOnDestroy() {
    this.nodeDetailsSub?.unsubscribe();
    this.billingDetailsSub?.unsubscribe();
    this.reloadNodeDetails?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getLocalTime(utcTime: string): string {
    if (!utcTime) return "NA";

    const date = new Date(utcTime);
    console.log(date)
    console.log(date.toLocaleString())
    return date.toLocaleString(); // Converts to local time automatically
  }

  loadNodeDetails(data) {
    if (data) {
      this.node = <Node>JSON.parse(JSON.stringify(data));
      if (
        [
          NodeTypes.TxtGpuMaster.toString(),
          NodeTypes.TxtGpuSlave.toString(),
          NodeTypes.TxtHeadNode.toString(),
          NodeTypes.TxtHead_Workstore.toString(),
          NodeTypes.TxtHwHost.toString(),
          NodeTypes.TxtLicenseServer.toString(),
          NodeTypes.TxtStorage.toString(),
          NodeTypes.TxtVPNServer.toString(),
          NodeTypes.TxtWorkstation.toString(),
          NodeTypes.TxtWorkstore.toString(),
        ].includes(this.node?.nodeType)
      ) {
        this.nodeGroupChangeAccess = false;
      } else {
        if (this.role == UserRoleNames.GlobalAdmin) {
          this.nodeGroupChangeAccess = true;
        }
      }
      if (
        this.role == UserRoleNames.GlobalAdmin ||
        this.role == UserRoleNames.DeploymentAdmin
      ) {
        this.rdProtocolChangeAccess = true;
      } else if (this.node?.mappedUsers?.length != 0) {
        let loggedUser = this.node.mappedUsers.find(
          (user) => user == this.loggedInUser
        );
        if (loggedUser) this.rdProtocolChangeAccess = true;
      } else {
        this.rdProtocolChangeAccess = false;
      }

      this.nodeDepName = !this.node.allDeploymentsList
        ? this.node.depName
        : this.node.allDeploymentsList;
      this.depName = this.node.depName;
      this.rdProtocol = this.node.rdProtocol;
      if (this.rdProtocol == "-1") {
        this.rdProtocolName = "Deployment Default";
      } else if (this.rdProtocol == "0") {
        this.rdProtocolName = "RDP or VNC";
      } else if (this.rdProtocol == "1") {
        this.rdProtocolName = "PCoIP";
      }
      this.cloudType = this.node.cloudType;
      let isxCATStr = this.configEntityService?.SyncHpcXMLParameters?.find(
        (param) => param.parameterName === "xCATSupport"
      )?.parameterValue;
      this.isxCAT = isxCATStr === "true";

      if (
        this.cloudType == CloudTypes.OnPremise &&
        this.role == UserRoleNames.GlobalAdmin &&
        this.isxCAT
      ) {
        this.showEdit = true;
      }
    }
    this.spinner.hide();
  }

  getTooltip(tooltipOf: string) {
    this.tooltipHtml = null;
    this.tooltipHtml = this.tooltipJson.getTooltipOf("nodes", tooltipOf);
  }

  getNodeGroup() {
    this.applicationService
      .getNodeGroupsForDeployment(this.depName)
      .subscribe((data) => {
        this.nodeGroups = <string[]>JSON.parse(JSON.stringify(data));
      });
  }

  setNodeGroup() {
    const dialogRef = this.dialog.open(SetNodegroupVmComponent, {
      autoFocus: false,
      disableClose: true,
      width: "450px",
      data: {
        nodeGroups: this.nodeGroups,
        vmNodeGroups: this.node.azureServiceName,
        depName: this.depName,
        nodeName: this.node.vmName,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.node.azureServiceName = res;
      }
    });
  }

  editNode() {
    const dialogRef = this.dialog.open(UpdateBmNodeComponent, {
      autoFocus: false,
      disableClose: true,
      width: "450px",
      data: {
        depName: this.node.depName,
        nodeName: this.node.vmName,
        currentUser: this.authenticationService.currentUserValue.username,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
      }
    });
  }

  updateRdProtocol(event) {
    this.nodeService
      .updateNodeRdProtocolForDeployment(
        event.target.value,
        this.node.vmName,
        this.depName
      )
      .subscribe({
        next(res) {
          this.snackBar.open(res.actionMessage, "Ok", {
            duration: 5000,
          });
        },
        error(msg) {
          this.snackBar.open(msg.error.actionMessage, "Ok", {
            duration: 5000,
          });
        },
      });
  }
}
